/**
 * <silo-real-kitchens> — "Silo in Real Kitchens": the PDP's UGC video carousel,
 * fed by Creatable's content API (the same public GraphQL endpoint the vendor
 * widget calls) and rendered on the theme's own markup
 * (snippets/silo-real-kitchens.liquid).
 *
 * Performance rules it keeps:
 *   - nothing loads until the section is within ~600px of the viewport
 *     (one IntersectionObserver), then one small GraphQL request;
 *   - the track's height is reserved by skeleton cards, so the feed replaces
 *     them without a layout shift;
 *   - exactly ONE card plays at a time — the active card, i.e. the one snapped
 *     at the start of the track (scrolling / the arrows hand playback over,
 *     tapping play on another card makes it active); a <video> is created only
 *     for a card that becomes active, and everything pauses while the section
 *     is off-screen; sound is one shared toggle;
 *   - every listener hangs on one AbortController, every observer is
 *     disconnected, every video released on disconnect.
 *
 * Attributes: data-source ('shopify' = cards server-rendered from the ugc_video
 * metaobjects, nothing fetched; 'creatable' = the legacy feed below),
 * data-endpoint (GraphQL URL incl. key), data-limit, data-sort
 * (conversion | date), data-handles ("content_id=@handle" per line — the API
 * has no creator field; without a mapping the chip shows the creator name
 * from the item title, before the first comma).
 */
(() => {
  if (customElements.get('silo-real-kitchens')) return;

  const QUERY = (limit, sort) => `{ content(per_page: ${limit}, filter: {hosting_platform: creatable, content_type: video}, sort: {name: ${sort}, direction: desc}) { total items { content_id title description asset { asset_type video { thumbnails { url width height } sources { mp4 { height width url } } } } } } }`;
  const NO_AUTOPLAY = window.matchMedia('(prefers-reduced-motion: reduce)').matches || (navigator.connection && navigator.connection.saveData);
  const SECTION_RATIO = 0.4; // below this much of the section in view, nothing plays
  const MAX_VIDEOS = 4;      // live <video> elements kept around (active + recent); older ones release their decoder
  // Lightbox warm-up: the best-quality file of ONE card is fetched ahead (metadata for the card that is playing,
  // more on hover/touch intent) so the lightbox starts instantly. Skipped on data saver, reduced motion and slow links.
  const conn = navigator.connection || {};
  const WARM = !NO_AUTOPLAY && !/(^|-)(2g|3g)$/.test(conn.effectiveType || '');
  const idle = window.requestIdleCallback ? (fn) => window.requestIdleCallback(fn, { timeout: 2000 }) : (fn) => setTimeout(fn, 600);

  class SiloRealKitchens extends HTMLElement {
    connectedCallback() {
      this._ac = new AbortController();
      const signal = this._ac.signal;
      this._track = this.querySelector('[data-rk-track]');
      this._tpl = this.querySelector('template[data-rk-card]');
      this._prev = this.querySelector('[data-rk-prev]');
      this._next = this.querySelector('[data-rk-next]');
      this._videos = new Map(); // card → video
      this._handles = this._parseHandles(this.dataset.handles || '');
      // the <template> only exists for the Creatable source; Shopify-rendered cards need no template
      if (!this._track || (this.dataset.source !== 'shopify' && !this._tpl)) return;

      // arrows + edge state
      this._prev && this._prev.addEventListener('click', () => this._page(-1), { signal });
      this._next && this._next.addEventListener('click', () => this._page(1), { signal });
      let raf = 0;
      this._track.addEventListener('scroll', () => {
        if (!raf) raf = requestAnimationFrame(() => { raf = 0; this._edges(); });
        clearTimeout(this._settle); this._settle = setTimeout(() => this._activate(this._pickActive()), 140); // when the scroll settles
      }, { signal, passive: true });
      if ('onscrollend' in window) this._track.addEventListener('scrollend', () => { clearTimeout(this._settle); this._activate(this._pickActive()); }, { signal, passive: true });
      if ('ResizeObserver' in window) { this._ro = new ResizeObserver(() => this._edges()); this._ro.observe(this._track); }

      // card controls (delegated)
      this.addEventListener('click', (e) => {
        const open = e.target.closest('[data-video-modal-open]');
        if (open) { e.preventDefault(); e.stopPropagation(); this._lightbox(open); } // we open the modal ourselves (hand-over of the warmed video)
      }, { signal });

      // intent → buffer the lightbox file of that card ahead of the click
      const intent = (e) => { const card = e.target.closest && e.target.closest('.silo-rk__card'); if (card) this._warm(card, 'auto'); };
      this.addEventListener('pointerenter', intent, { signal, capture: true, passive: true });
      this.addEventListener('touchstart', intent, { signal, capture: true, passive: true });
      this.addEventListener('focusin', intent, { signal });

      // keyboard on the track: arrows page
      this._track.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') { e.preventDefault(); this._page(1); } else if (e.key === 'ArrowLeft') { e.preventDefault(); this._page(-1); } }, { signal });

      // Shopify source: the cards are already in the DOM — wire them up, no request.
      if (this.dataset.source === 'shopify') { this._wire(); return; }

      // Creatable source: load the feed once the section nears the viewport
      const start = () => this._load();
      if ('IntersectionObserver' in window) {
        this._loadIo = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) { this._loadIo.disconnect(); this._loadIo = null; start(); } }, { rootMargin: '600px 0px' });
        this._loadIo.observe(this);
      } else start();
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._modalMo) { this._modalMo.disconnect(); this._modalMo = null; }
      this._releaseWarm();
      if (this._loadIo) { this._loadIo.disconnect(); this._loadIo = null; }
      if (this._viewIo) { this._viewIo.disconnect(); this._viewIo = null; }
      clearTimeout(this._settle);
      if (this._ro) { this._ro.disconnect(); this._ro = null; }
      this._videos.forEach((v, card) => { try { v.pause(); v.removeAttribute('src'); v.load(); v.remove(); card.dataset.state = 'idle'; } catch (e) { /* ignore */ } });
      this._videos.clear();
    }

    /* ---------- data ---------- */
    async _load() {
      const endpoint = this.dataset.endpoint;
      const limit = Math.min(40, Math.max(1, parseInt(this.dataset.limit, 10) || 16));
      const sort = this.dataset.sort === 'date' ? 'date' : 'conversion';
      let items = [];
      try {
        const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: QUERY(limit, sort) }), signal: this._ac.signal, credentials: 'omit' });
        if (!res.ok) throw new Error('creatable ' + res.status);
        const json = await res.json();
        items = ((json && json.data && json.data.content && json.data.content.items) || []).filter((it) => it && it.asset && it.asset.video && it.asset.video.sources);
      } catch (err) {
        if (this._ac.signal.aborted) return;
        items = [];
      }
      if (this._ac.signal.aborted) return;
      if (!items.length) { this.hidden = true; return; } // nothing to show: the section folds away
      this._render(items);
    }

    _parseHandles(text) {
      const map = {};
      text.split(/\n|;/).forEach((line) => { const m = line.match(/^\s*(\d+)\s*=\s*(.+?)\s*$/); if (m) map[m[1]] = m[2]; });
      return map;
    }

    _label(item) {
      const mapped = this._handles[String(item.content_id)];
      if (mapped) return mapped.startsWith('@') ? mapped : '@' + mapped;
      const t = String(item.title || '').split(/[,|–\-_(]/)[0].trim();
      return t ? '@' + t.replace(/^@/, '') : '';
    }

    _pickMp4(item, hd) {
      const list = (item.asset.video.sources.mp4 || []).slice().sort((a, b) => a.height - b.height);
      if (hd) return (list[list.length - 1] || {}).url || ''; // the lightbox gets the best quality
      return (list.find((s) => s.height >= 720) || list[list.length - 1] || {}).url || '';
    }

    _pickPoster(item) {
      const th = (item.asset.video.thumbnails || []).slice().sort((a, b) => a.width - b.width);
      return (th.find((t) => t.width >= 400) || th[th.length - 1] || {}).url || '';
    }

    /* ---------- render ---------- */
    _render(items) {
      const frag = document.createDocumentFragment();
      items.forEach((item, i) => {
        const node = this._tpl.content.firstElementChild.cloneNode(true);
        node.dataset.src = this._pickMp4(item);
        node.dataset.id = String(item.content_id);
        const open = node.querySelector('[data-video-modal-open]');
        if (open) open.dataset.videoUrl = this._pickMp4(item, true);
        const img = node.querySelector('.silo-rk__poster');
        img.src = this._pickPoster(item);
        img.sizes = '(max-width: 767px) 56vw, 260px';
        img.alt = (item.title || '').slice(0, 80);
        if (i < 2) { img.loading = 'eager'; img.fetchPriority = 'high'; }
        const chip = node.querySelector('[data-rk-chip]');
        chip.textContent = this._label(item);
        node.setAttribute('tabindex', '0');
        node.setAttribute('role', 'listitem');
        frag.appendChild(node);
      });
      this._track.replaceChildren(frag);
      this._track.setAttribute('aria-busy', 'false');
      this._wire();
    }

    /* cards are in the DOM (either source): arrows, edge state, in-view playback */
    _wire() {
      this._edges();

      // one observer on the section: in view → the active card plays; out of view → everything pauses
      this._inView = false;
      if ('IntersectionObserver' in window) {
        this._viewIo = new IntersectionObserver((entries) => {
          const en = entries[entries.length - 1];
          this._inView = en.intersectionRatio >= SECTION_RATIO;
          if (this._inView) this._activate(this._active || this._pickActive()); else this._pauseAll();
        }, { threshold: [0, SECTION_RATIO] });
        this._viewIo.observe(this._track);
      } else { this._inView = true; this._activate(this._pickActive()); }
    }

    /* ---------- playback ---------- */
    _video(card) {
      let v = this._videos.get(card);
      if (v) return v;
      v = document.createElement('video');
      v.className = 'silo-rk__video';
      v.muted = true; // inline cards are always silent; the lightbox has sound
      v.playsInline = true; v.setAttribute('playsinline', ''); v.setAttribute('disablepictureinpicture', '');
      v.loop = false; v.preload = 'metadata'; // no loop: the end of a video hands over to the next card
      v.src = card.dataset.src;
      v.addEventListener('playing', () => { card.dataset.state = 'playing' }, { signal: this._ac.signal });
      v.addEventListener('pause', () => { if (card.dataset.state !== 'paused') card.dataset.state = 'idle'; }, { signal: this._ac.signal });
      // progress bar: timeupdate fires ~4×/s; the fill scales on the compositor
      v.addEventListener('timeupdate', () => { if (v.duration) card.style.setProperty('--rk-progress', (v.currentTime / v.duration).toFixed(4)); }, { signal: this._ac.signal });
      v.addEventListener('ended', () => this._advance(card), { signal: this._ac.signal });
      // a restart must snap back, not glide backwards
      v.addEventListener('seeked', () => { if (v.currentTime < 0.05) { card.classList.add('is-restarting'); card.style.setProperty('--rk-progress', '0'); requestAnimationFrame(() => card.classList.remove('is-restarting')); } }, { signal: this._ac.signal });
      card.querySelector('.silo-rk__poster').after(v);
      this._videos.set(card, v);
      this._trim(); // the cap counts this new one too
      return v;
    }

    /* the card snapped at the track's start — the one the visitor is looking at */
    _pickActive() {
      const cards = this._track.querySelectorAll('.silo-rk__card:not(.silo-rk__card--skeleton)');
      if (!cards.length) return null;
      const start = this._track.getBoundingClientRect().left + (parseFloat(getComputedStyle(this._track).paddingLeft) || 0);
      let best = null, dist = Infinity;
      cards.forEach((c) => { const d = Math.abs(c.getBoundingClientRect().left - start); if (d < dist) { dist = d; best = c; } });
      return best;
    }

    _activate(card) {
      if (!card || !this._inView) return;
      if (this._active && this._active !== card) this._pause(this._active, true);
      this._active = card;
      if (card.dataset.state === 'paused') return; // the visitor paused this one: respect it until they press play
      if (NO_AUTOPLAY) return;
      this._play(card);
      idle(() => { if (this._active === card && this._inView) this._warm(card, 'metadata'); });
    }

    /* ---------- lightbox warm-up ---------- */
    _warm(card, level) {
      if (!WARM || !card) return;
      const btn = card.querySelector('[data-video-modal-open]');
      const url = btn && btn.getAttribute('data-video-url');
      if (!url) return;
      let w = this._warmVideo;
      if (w && w.getAttribute('src') !== url) { this._releaseWarm(); w = null; }
      if (!w) {
        w = document.createElement('video');
        w.muted = true; w.playsInline = true; w.setAttribute('playsinline', '');
        w.preload = level; w.setAttribute('src', url);
        w.setAttribute('data-rk-warm', ''); w.hidden = true; w.style.display = 'none';
        this.appendChild(w); // in the DOM so every engine actually fetches it; invisible, never played here
        this._warmVideo = w;
        if (level === 'auto') w.load();
      } else if (level === 'auto' && w.preload !== 'auto') { w.preload = 'auto'; w.load(); }
    }

    _releaseWarm() {
      const w = this._warmVideo; if (!w) return;
      this._warmVideo = null;
      try { w.removeAttribute('src'); w.load(); } catch (e) { /* ignore */ }
      w.remove();
    }

    /* the active video ended: bring the next card to the front (it becomes active and plays); the last wraps to the first */
    _advance(card) {
      if (!this._inView || this._active !== card) return;
      const cards = [...this._track.querySelectorAll('.silo-rk__card:not(.silo-rk__card--skeleton)')];
      const next = card.nextElementSibling && card.nextElementSibling.classList.contains('silo-rk__card') ? card.nextElementSibling : cards[0];
      if (!next || next === card) { const v = this._videos.get(card); if (v) { v.currentTime = 0; this._play(card); } return; }
      const left = next.offsetLeft - (parseFloat(getComputedStyle(this._track).paddingLeft) || 0);
      this._track.scrollTo({ left, behavior: 'smooth' });
      // a settled scroll activates the next card; if the track cannot move (already there), activate now
      clearTimeout(this._settle); this._settle = setTimeout(() => this._activate(this._pickActive()), 160);
    }

    /* the lightbox takes over: silence the inline card while it is open, hand playback back when it closes */
    _lightbox(trigger) {
      const modal = document.getElementById(trigger.getAttribute('data-video-modal-open'));
      if (!modal || !trigger.getAttribute('data-video-url')) return;
      // the PDP's product column is a containing block (sticky/transform), which would trap the fixed lightbox: hoist it to <body> once
      if (modal.parentElement !== document.body) document.body.appendChild(modal);
      this._pauseAll();
      if (this._modalMo) this._modalMo.disconnect();
      this._modalMo = new MutationObserver(() => {
        if (!modal.hidden) return;
        this._modalMo.disconnect(); this._modalMo = null;
        if (this._inView && this._active) this._activate(this._active);
      });
      this._modalMo.observe(modal, { attributes: true, attributeFilter: ['hidden'] });
      // hand the warmed <video> over (same url) so the lightbox starts from the buffer; the modal owns and releases it from here
      const url = trigger.getAttribute('data-video-url');
      let warm = this._warmVideo && this._warmVideo.getAttribute('src') === url ? this._warmVideo : null;
      if (warm) { this._warmVideo = null; warm.hidden = false; warm.style.display = ''; warm.removeAttribute('data-rk-warm'); }
      if (typeof modal.open === 'function') modal.open(url, warm || undefined);
    }

    _pauseAll() { this._videos.forEach((v, card) => { if (!v.paused) this._pause(card, true); }); }

    _play(card) {
      const v = this._video(card);
      if (!v.paused) return;
      if (v.ended) v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch(() => { card.dataset.state = 'idle' });
    }

    _pause(card, byScroll) {
      const v = this._videos.get(card);
      if (!v) return;
      v.pause();
      card.dataset.state = byScroll ? 'idle' : 'paused';
      this._trim();
    }

    /* keep at most MAX_VIDEOS <video> elements alive: the active card and the most recent others; the rest go back to their poster */
    _trim() {
      if (this._videos.size <= MAX_VIDEOS) return;
      for (const [card, v] of this._videos) {
        if (this._videos.size <= MAX_VIDEOS) break;
        if (card === this._active || !v.paused) continue;
        try { v.pause(); v.removeAttribute('src'); v.load(); } catch (e) { /* ignore */ }
        v.remove();
        this._videos.delete(card);
        card.dataset.state = 'idle'; card.style.removeProperty('--rk-progress')
      }
    }

    /* ---------- navigation ---------- */
    _step() {
      const card = this._track.querySelector('.silo-rk__card');
      const gap = parseFloat(getComputedStyle(this._track).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : this._track.clientWidth;
    }

    _page(dir) {
      this._track.scrollBy({ left: dir * this._step(), behavior: 'smooth' });
    }

    _edges() {
      const t = this._track;
      const max = t.scrollWidth - t.clientWidth;
      if (this._prev) this._prev.disabled = t.scrollLeft <= 2;
      if (this._next) this._next.disabled = t.scrollLeft >= max - 2; // sub-pixel scroll positions count as the end
    }
  }

  customElements.define('silo-real-kitchens', SiloRealKitchens);
})();
